import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import type { AssistContext } from '@/shared/assist/AssistContract';
import type { DashboardComposeRequest, DashboardLayoutId, DashboardStreamFrame, DashboardTileConfig } from '@/shared/assist/DashboardContract';
import { isValidDashboardTileConfig } from './AIExplorerTileTypes';
import { findLayout } from './AIExplorerLayouts';
import { IAIExplorerStreamSourceToken, type AIExplorerStreamHandle, type IAIExplorerStreamSource } from './AIExplorerStreamSource';

export type AIExplorerStatus = 'idle' | 'composing' | 'ready' | 'error';

/**
 * Drives the progressive, streamed population of a dynamic dashboard. On
 * {@link compose} it opens a stream and, as frames arrive, fills the layout then
 * each slot — modelled on the signal grid's seed-then-swap engine but driven by
 * configs ARRIVING over the wire rather than a static def list. Each tile renders
 * the moment its config lands; the tile then self-loads its own data. A new
 * compose cancels the previous stream (run-id guarded), so a re-prompt never
 * interleaves stale tiles.
 */
@injectable()
export class AIExplorerViewModel extends BaseViewModel {
    status: AIExplorerStatus = 'idle';
    layoutId: DashboardLayoutId | null = null;
    title: string | null = null;
    subtitle: string | null = null;
    slotIds: string[] = [];
    tiles: Record<string, DashboardTileConfig> = {};
    note: string | null = null;
    error: string | null = null;
    lastPrompt = '';

    private handle: AIExplorerStreamHandle | null = null;
    private runId = 0;

    constructor(@inject(IAIExplorerStreamSourceToken) private readonly source: IAIExplorerStreamSource) {
        super();
    }

    get tileCount(): number {
        return Object.keys(this.tiles).length;
    }

    compose(prompt: string, context: AssistContext): void {
        const trimmed = prompt.trim();
        if (trimmed.length === 0) return;
        this.cancel();
        const runId = ++this.runId;

        this.status = 'composing';
        this.lastPrompt = trimmed;
        this.layoutId = null;
        this.title = null;
        this.subtitle = null;
        this.slotIds = [];
        this.tiles = {};
        this.note = null;
        this.error = null;
        this.notifyChange();

        const request: DashboardComposeRequest = { prompt: trimmed, context };
        this.handle = this.source.open(request, {
            onFrame: (frame) => {
                if (this.runId !== runId) return;
                this.applyFrame(frame);
            },
            onError: (message) => {
                if (this.runId !== runId) return;
                this.status = 'error';
                this.error = message;
                this.notifyChange();
            },
            onClose: () => {
                if (this.runId !== runId) return;
                if (this.status === 'composing') {
                    this.status = 'ready';
                    this.notifyChange();
                }
            },
        });
    }

    private applyFrame(frame: DashboardStreamFrame): void {
        switch (frame.type) {
            case 'layout':
                if (findLayout(frame.layoutId) === undefined) {
                    this.status = 'error';
                    this.error = `The composer chose an unknown layout ("${frame.layoutId}").`;
                    break;
                }
                this.layoutId = frame.layoutId;
                this.title = frame.title;
                this.subtitle = frame.subtitle ?? null;
                this.slotIds = frame.slotIds;
                break;
            case 'tile':
                if (isValidDashboardTileConfig(frame.tile)) {
                    this.tiles = { ...this.tiles, [frame.slotId]: frame.tile };
                }
                break;
            case 'note':
                this.note = frame.message;
                break;
            case 'done':
                this.status = 'ready';
                break;
            case 'error':
                this.status = 'error';
                this.error = frame.message;
                break;
        }
        this.notifyChange();
    }

    cancel(): void {
        if (this.handle !== null) {
            this.handle.close();
            this.handle = null;
        }
    }

    /** Returns to the idle starter state, cancelling any in-flight compose (run-id bump drops late frames). */
    reset(): void {
        this.cancel();
        this.runId += 1;
        this.status = 'idle';
        this.layoutId = null;
        this.title = null;
        this.subtitle = null;
        this.slotIds = [];
        this.tiles = {};
        this.note = null;
        this.error = null;
        this.lastPrompt = '';
        this.notifyChange();
    }

    override dispose(): void {
        this.cancel();
        super.dispose();
    }
}
